import React, { useCallback, useState } from 'react';
import {
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import { NODE_HEIGHT_XL } from 'flow/service/graph';
import type { SnippetNode as ISnippetNode, WorkflowNodeProps } from 'flow/service/graph';
import BaseNode from '../BaseNode';
import {
  StyledDropNode,
  StyledLabelContainer,
  StyledLabelText,
  StyledSnippetIconContainer,
  StyledVersionBadge,
  snippetFocusedStyle,
} from './styles';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectActionByPublicId, selectNodeState, selectSnippetCollapsedState } from 'flow/store/redux/selectors';
import { toggleSnippetCollapsibleThunk } from 'flow/store/redux/thunks';

function SnippetNode(props: WorkflowNodeProps<ISnippetNode>) {
  const { id, type } = props;
  const { isCollapsed } = useAppSelector(state => selectSnippetCollapsedState(id)(state));
  const nodeState = useAppSelector(state => selectNodeState(state, id));
  const snippetAction = useAppSelector(selectActionByPublicId(id));
  const dispatch = useAppDispatch();

  const version = snippetAction?.snippet?.publicVersion;

  // Todo:
  // - dynamically load in snippet configurations
  // - when expanded, add in background behind a snippet's laid out childen

  const [isIconHovered, setIsIconHovered] = useState(false);

  const handleIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleSnippetCollapsibleThunk(id));
  }, [dispatch, id]);

  const SnippetIcon = (
    <StyledSnippetIconContainer
      onMouseEnter={() => setIsIconHovered(true)}
      onMouseLeave={() => setIsIconHovered(false)}
      onClick={handleIconClick}
    >
      <Icon
        icon={isIconHovered ? IconName.UpRightDownLeftFromCenter : IconName.AddressCard}
        size="large"
        color="var(--cl-gray-000)"
      />
    </StyledSnippetIconContainer>
  );

  const labelText = isCollapsed && version != null
    ? `Linked Snippet v${version}`
    : 'Linked Snippet';

  const Label = (
    <StyledLabelContainer>
      <Text size={1} style={{ color: 'var(--cl-text-muted)' }}>
        {labelText}
      </Text>
      <StyledLabelText>
        Snippet Name
      </StyledLabelText>
    </StyledLabelContainer>
  );

  const VersionBadge = (
    <StyledVersionBadge>
      v{version}
    </StyledVersionBadge>
  );


  return (
    <>
      <BaseNode
        id={id}
        icon={SnippetIcon}
        label={Label}
        style={{
          borderRadius: 'var(--cl-radius-3)',
          border: '1px solid var(--cl-gray-300)',
          height: `${NODE_HEIGHT_XL}px`,
          padding: 'var(--cl-space-1) var(--cl-space-2)',
        }}
        stateStyle={{
          baseBackgroundColor: 'var(--cl-gray-000)',
          highlighted: {
            backgroundColor: 'var(--cl-gray-000)',
          },
          hovered: {
            backgroundColor: 'var(--cl-gray-100)',
          },
          focused: snippetFocusedStyle,
          'focused-hovered': snippetFocusedStyle,
        }}
        type={type}
        disable={{ nodeMenu: isCollapsed, nodeMenuHoverState: !isCollapsed }}
      >
        {!isCollapsed ? VersionBadge : undefined}
      </BaseNode>
      {
        /** Conditionally render a backdrop node, layered behind the main BaseNode. */
        isCollapsed && <StyledDropNode nodeState={nodeState} />
      }
    </>
  );
}

export default React.memo(SnippetNode);
